import { css } from 'aphrodite';
import * as React from 'react';
import { forwardRef } from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { TextArea } from '../TextArea';
import { s } from './styles';

function InputGroup({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<'div'>>) {
	return <div data-slot='input-group' role='group' className={css(s.inputGroup, ...styles)} {...props} />;
}

function InputGroupAddon({
	styles = [],
	align = 'inlineStart',
	...props
}: AphroditeStyled<React.ComponentProps<'div'>> & { align?: 'inlineStart' | 'inlineEnd' | 'blockStart' | 'blockEnd' }) {
	return (
		<div
			role='group'
			data-slot='input-group-addon'
			data-align={align}
			className={css(s.inputGroupAddon, s[`inputGroupAddon${align}`], ...styles)}
			onClick={e => {
				if ((e.target as HTMLElement).closest('button')) {
					return;
				}
				e.currentTarget.parentElement?.querySelector('input')?.focus();
			}}
			{...props}
		/>
	);
}

function InputGroupButton({
	styles = [],
	type = 'button',
	variant = 'ghost',
	size = 'xs',
	...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> & { size?: 'xs' | 'sm' | 'iconXs' | 'iconSm' }) {
	return (
		<Button
			type={type}
			data-size={size}
			variant={variant}
			styles={[s.inputGroupButton, s[`inputGroupButton${size}`], ...styles]}
			{...props}
		/>
	);
}

function InputGroupText({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<'span'>>) {
	return <span className={css(s.inputGroupText, ...styles)} {...props} />;
}

const InputGroupInput = forwardRef<HTMLInputElement, AphroditeStyled<React.ComponentProps<'input'>>>(
	({ styles = [], ...props }, ref) => {
		return <Input data-slot='input-group-control' styles={[s.inputGroupInput, ...styles]} ref={ref} {...props} />;
	}
);
InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = forwardRef<HTMLTextAreaElement, AphroditeStyled<React.ComponentProps<'textarea'>>>(
	({ styles = [], ...props }, ref) => {
		return <TextArea data-slot='input-group-control' styles={[s.inputGroupTextarea, ...styles]} ref={ref} {...props} />;
	}
);
InputGroupTextarea.displayName = 'InputGroupTextarea';

export {
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupText,
	InputGroupTextarea,
	InputGroup as InputGroupV2,
};
